<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Payslips</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Payslips</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading payslips...</p>
          </div>

          <div v-else-if="payslipsData">
            <!-- YTD Summary Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                          <i class="mdi mdi-cash-multiple"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">YTD Gross</p>
                        <h4 class="mb-0">KES {{ formatCurrency(payslipsData.ytdSummary.totalGross) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                          <i class="mdi mdi-wallet"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">YTD Net</p>
                        <h4 class="mb-0">KES {{ formatCurrency(payslipsData.ytdSummary.totalNet) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-danger-subtle text-danger rounded-circle fs-3">
                          <i class="mdi mdi-minus-circle"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">YTD Deductions</p>
                        <h4 class="mb-0">KES {{ formatCurrency(payslipsData.ytdSummary.totalDeductions) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                          <i class="mdi mdi-file-document"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Payslips</p>
                        <h4 class="mb-0">{{ payslipsData.ytdSummary.payslipCount }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Latest Payslip Highlight -->
            <div v-if="payslipsData.latestPayslip" class="row">
              <div class="col-12">
                <div class="card bg-primary bg-gradient text-white">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h5 class="text-white mb-2">
                          <i class="mdi mdi-star me-2"></i>
                          Latest Payslip - {{ payslipsData.latestPayslip.period_label }}
                        </h5>
                        <div class="row">
                          <div class="col-sm-4 mb-2">
                            <p class="text-white-50 mb-1 small">Gross Salary</p>
                            <h5 class="text-white mb-0">KES {{ formatCurrency(payslipsData.latestPayslip.gross_salary) }}</h5>
                          </div>
                          <div class="col-sm-4 mb-2">
                            <p class="text-white-50 mb-1 small">Deductions</p>
                            <h5 class="text-white mb-0">KES {{ formatCurrency(payslipsData.latestPayslip.total_deductions) }}</h5>
                          </div>
                          <div class="col-sm-4 mb-2">
                            <p class="text-white-50 mb-1 small">Net Pay</p>
                            <h5 class="text-white mb-0">KES {{ formatCurrency(payslipsData.latestPayslip.net_salary) }}</h5>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <button
                            @click="viewPayslip(payslipsData.latestPayslip)"
                            class="btn btn-light btn-lg me-2">
                          <i class="mdi mdi-eye me-1"></i>
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-md-3">
                        <label class="form-label">Filter by Year</label>
                        <select v-model="filters.year" @change="loadPayslips" class="form-select">
                          <option value="">All Years</option>
                          <option v-for="year in payslipsData.availableYears" :key="year" :value="year">
                            {{ year }}
                          </option>
                        </select>
                      </div>
                      <div class="col-md-3">
                        <label class="form-label">Filter by Month</label>
                        <select v-model="filters.month" @change="loadPayslips" class="form-select">
                          <option value="">All Months</option>
                          <option value="1">January</option>
                          <option value="2">February</option>
                          <option value="3">March</option>
                          <option value="4">April</option>
                          <option value="5">May</option>
                          <option value="6">June</option>
                          <option value="7">July</option>
                          <option value="8">August</option>
                          <option value="9">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>
                      <div class="col-md-6 d-flex align-items-end">
                        <button @click="resetFilters" class="btn btn-outline-secondary">
                          <i class="mdi mdi-refresh me-1"></i>
                          Reset Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payslips List -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-file-document-multiple text-primary me-2"></i>
                      All Payslips
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="payslipsData.payslips && payslipsData.payslips.length > 0">
                      <div class="table-responsive">
                        <table class="table table-hover mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>Period</th>
                            <th>Payment Date</th>
                            <th class="text-end">Gross Salary</th>
                            <th class="text-end">Deductions</th>
                            <th class="text-end">Net Pay</th>
                            <th class="text-center">Status</th>
                            <th class="text-center">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="payslip in payslipsData.payslips" :key="payslip.id">
                            <td>
                              <strong>{{ payslip.period_label }}</strong>
                              <br>
                              <small class="text-muted">
                                {{ formatDate(payslip.payroll_period_start) }} - {{ formatDate(payslip.payroll_period_end) }}
                              </small>
                            </td>
                            <td>
                                <span class="badge bg-info-subtle text-info">
                                  {{ payslip.payment_date_formatted }}
                                </span>
                            </td>
                            <td class="text-end">
                              <strong>KES {{ formatCurrency(payslip.gross_salary) }}</strong>
                            </td>
                            <td class="text-end text-danger">
                              KES {{ formatCurrency(payslip.total_deductions) }}
                            </td>
                            <td class="text-end">
                              <strong class="text-success">KES {{ formatCurrency(payslip.net_salary) }}</strong>
                            </td>
                            <td class="text-center">
                                <span :class="getStatusBadgeClass(payslip.payment_status)">
                                  {{ payslip.payment_status || 'Paid' }}
                                </span>
                            </td>
                            <td class="text-center">
                              <div class="btn-group btn-group-sm">
                                <button
                                    @click="viewPayslip(payslip)"
                                    class="btn btn-outline-primary"
                                    title="View Details">
                                  <i class="mdi mdi-eye"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Pagination -->
                      <div v-if="payslipsData.pagination.totalPages > 1" class="mt-4 d-flex justify-content-between align-items-center">
                        <div>
                          Showing {{ (payslipsData.pagination.currentPage - 1) * payslipsData.pagination.limit + 1 }}
                          to {{ Math.min(payslipsData.pagination.currentPage * payslipsData.pagination.limit, payslipsData.pagination.totalRecords) }}
                          of {{ payslipsData.pagination.totalRecords }} payslips
                        </div>
                        <nav>
                          <ul class="pagination pagination-sm mb-0">
                            <li class="page-item" :class="{disabled: payslipsData.pagination.currentPage === 1}">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(payslipsData.pagination.currentPage - 1)">
                                <i class="mdi mdi-chevron-left"></i>
                              </a>
                            </li>
                            <li
                                v-for="page in visiblePages"
                                :key="page"
                                class="page-item"
                                :class="{active: page === payslipsData.pagination.currentPage}">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(page)">
                                {{ page }}
                              </a>
                            </li>
                            <li class="page-item" :class="{disabled: payslipsData.pagination.currentPage === payslipsData.pagination.totalPages}">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(payslipsData.pagination.currentPage + 1)">
                                <i class="mdi mdi-chevron-right"></i>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>

                    <div v-else class="text-center text-muted py-5">
                      <i class="mdi mdi-file-document-outline font-size-48"></i>
                      <p class="mt-2 mb-0">No payslips found</p>
                      <small>Payslips will appear here once payroll is processed</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>

    <!-- Payslip Details Modal -->
    <Payslip
        ref="payslipModal"
        :transaction-id="selectedPayslip ? selectedPayslip.id : null"
    />

  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import Payslip from "@/components/payroll/Payslip.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeePayrollPayslips",
  components: { NavigationBar, Payslip },

  data() {
    return {
      loading: false,
      payslipsData: null,
      selectedPayslip: null,
      filters: {
        year: new Date().getFullYear(),
        month: '',
        page: 1
      }
    };
  },

  computed: {
    visiblePages() {
      if (!this.payslipsData || !this.payslipsData.pagination) return [];

      const current = this.payslipsData.pagination.currentPage;
      const total = this.payslipsData.pagination.totalPages;
      const pages = [];

      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  mounted() {
    this.loadPayslips();
  },

  methods: {
    async loadPayslips() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append('function', 'getEmployeePayslips');
        if (this.filters.year) data.append('year', this.filters.year);
        if (this.filters.month) data.append('month', this.filters.month);
        data.append('page', this.filters.page);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.payslipsData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load payslips');
        }
      } catch (error) {
        this.$toast?.error("Failed to load payslips: " + error.message) ||
        console.error("Failed to load payslips:", error);
      } finally {
        this.loading = false;
      }
    },

    initPayslipDetailsModal() {
      const modalElement = this.$refs.payslipDetailsModal;
      if (modalElement && typeof bootstrap !== 'undefined') {
        this.payslipDetailsModalInstance = new bootstrap.Modal(modalElement);
      }
    },

    viewPayslip(payslip) {
      // Use existing getTransactionDetails which already handles everything
      this.selectedPayslip = payslip;

      // Open payslip modal using the transaction ID
      this.$nextTick(() => {
        if (this.$refs.payslipModal) {
          this.$refs.payslipModal.showModal(payslip.id);
        } else {
          // Fallback: initialize modal
          const payslipModal = document.getElementById('payslipDetailsModal');
          if (payslipModal) {
            const modal = new bootstrap.Modal(payslipModal);
            modal.show();
          }
        }
      });
    },

    resetFilters() {
      this.filters.year = new Date().getFullYear();
      this.filters.month = '';
      this.filters.page = 1;
      this.loadPayslips();
    },

    changePage(page) {
      if (page < 1 || page > this.payslipsData.pagination.totalPages) return;
      this.filters.page = page;
      this.loadPayslips();
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    getStatusBadgeClass(status) {
      const statusMap = {
        'Paid': 'badge bg-success',
        'Pending': 'badge bg-warning',
        'Processing': 'badge bg-info',
        'Failed': 'badge bg-danger'
      };
      return statusMap[status] || 'badge bg-success';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>